<template>
  <AiEducationLayout>
    <div class="ai-edu-admin-page">
      <ContentWrap title="成绩双审">
        <el-form :inline="true" :model="query" class="-mb-15px">
          <el-form-item label="学校">
            <el-select
              v-model="query.schoolId"
              class="!w-200px"
              clearable
              filterable
              @change="onSchoolChange"
            >
              <el-option v-for="s in schools" :key="s.id" :label="s.name" :value="s.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="教学班">
            <el-select
              v-model="query.teachingClassId"
              class="!w-220px"
              clearable
              filterable
              @change="loadGradebook"
            >
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button
              v-hasPermi="['ai-education:gradebook:write']"
              :disabled="!query.teachingClassId"
              type="primary"
              @click="loadGradebook"
            >
              加载/创建成绩册
            </el-button>
          </el-form-item>
        </el-form>
      </ContentWrap>

      <ContentWrap v-if="gradebook">
        <div class="mb-12px flex flex-wrap items-center gap-12px">
          <span class="text-16px font-600">{{ gradebook.title || '成绩册' }}</span>
          <dict-tag :type="DICT_TYPE.AI_EDU_GRADEBOOK_STATUS" :value="gradebook.status" />
          <span class="text-13px text-gray-500"
            >权重 平时{{ gradebook.usualWeight }} / 作业{{ gradebook.homeworkWeight }} / 考试{{
              gradebook.examWeight
            }}</span
          >
          <span v-if="gradebook.submitTime" class="text-13px text-gray-500"
            >提交 {{ gradebook.submitTime }}</span
          >
          <span v-if="gradebook.publishTime" class="text-13px text-gray-500"
            >发布 {{ gradebook.publishTime }}</span
          >
        </div>
        <div class="mb-12px flex flex-wrap gap-8px">
          <el-button
            v-hasPermi="['ai-education:gradebook:write']"
            :loading="saving"
            type="primary"
            @click="saveScores"
            >保存成绩</el-button
          >
          <el-button
            v-hasPermi="['ai-education:gradebook:write']"
            :disabled="gradebook.status !== 0 && gradebook.status !== 40"
            @click="handleSubmit"
            >提交审核</el-button
          >
          <el-button
            v-hasPermi="['ai-education:gradebook:first-review']"
            type="success"
            :disabled="gradebook.status !== 10"
            @click="handleFirstReview"
            >初审通过</el-button
          >
          <el-button
            v-hasPermi="['ai-education:gradebook:final-review']"
            type="success"
            :disabled="gradebook.status !== 20"
            @click="handleFinalReview"
            >终审发布</el-button
          >
          <el-button
            v-hasPermi="['ai-education:gradebook:first-review', 'ai-education:gradebook:final-review']"
            type="danger"
            plain
            :disabled="![10, 20].includes(gradebook.status)"
            @click="openReject"
            >驳回</el-button
          >
        </div>
        <el-alert
          v-if="gradebook.rejectReason"
          :title="`驳回原因：${gradebook.rejectReason}`"
          type="error"
          :closable="false"
          class="mb-12px"
        />
        <el-table v-loading="loading" :data="scores" border>
          <el-table-column label="学生" min-width="140">
            <template #default="{ row }">
              <el-select
                v-if="!row.id"
                v-model="row.studentId"
                class="w-100%"
                filterable
                remote
                :remote-method="searchStudents"
              >
                <el-option
                  v-for="s in students"
                  :key="s.id"
                  :label="s.nickname || s.mobile || `#${s.id}`"
                  :value="s.id"
                />
              </el-select>
              <span v-else>{{ studentLabel(row.studentId) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="平时" width="120">
            <template #default="{ row }">
              <el-input-number v-model="row.usualScore" :max="100" :min="0" :precision="1" size="small" />
            </template>
          </el-table-column>
          <el-table-column label="作业" width="120">
            <template #default="{ row }">
              <el-input-number
                v-model="row.homeworkScore"
                :max="100"
                :min="0"
                :precision="1"
                size="small"
              />
            </template>
          </el-table-column>
          <el-table-column label="考试" width="120">
            <template #default="{ row }">
              <el-input-number v-model="row.examScore" :max="100" :min="0" :precision="1" size="small" />
            </template>
          </el-table-column>
          <el-table-column prop="totalScore" label="总分" width="90" />
          <el-table-column label="缺考" width="80">
            <template #default="{ row }"><el-switch v-model="row.absentExam" /></template>
          </el-table-column>
          <el-table-column label="备注" min-width="140">
            <template #default="{ row }"><el-input v-model="row.remark" size="small" /></template>
          </el-table-column>
        </el-table>
        <div class="mt-12px">
          <el-button
            v-hasPermi="['ai-education:gradebook:write']"
            plain
            @click="addScoreRow"
            >添加学生</el-button
          >
        </div>
      </ContentWrap>
      <ContentWrap v-else>
        <el-empty description="请选择教学班后加载或创建成绩册" />
      </ContentWrap>

      <Dialog v-model="rejectVisible" title="驳回成绩册" width="440px">
        <el-input v-model="rejectReason" type="textarea" placeholder="请输入驳回原因" :rows="3" />
        <template #footer>
          <el-button @click="rejectVisible = false">取消</el-button>
          <el-button :loading="saving" type="danger" @click="submitReject">确认驳回</el-button>
        </template>
      </Dialog>
    </div>
  </AiEducationLayout>
</template>

<script lang="ts" setup>
import * as AiEducationApi from '@/api/aiEducation'
import * as UniversityApi from '@/api/aiEducation/university'
import AiEducationLayout from '../../layout/AiEducationLayout.vue'
import request from '@/config/axios'
import { DICT_TYPE } from '@/utils/dict'

defineOptions({ name: 'AiEduGradebook' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const schools = ref<AiEducationApi.SchoolVO[]>([])
const teachingClasses = ref<AiEducationApi.TeachingClassVO[]>([])
const students = ref<Array<{ id: number; nickname?: string; mobile?: string }>>([])
const gradebook = ref<UniversityApi.GradebookVO | null>(null)
const scores = ref<UniversityApi.GradeScoreVO[]>([])
const query = reactive<{ schoolId?: number; teachingClassId?: number }>({})
const rejectVisible = ref(false)
const rejectReason = ref('')

const studentLabel = (id: number) => {
  const s = students.value.find((item) => item.id === id)
  return s?.nickname || s?.mobile || `学生 #${id}`
}

const onSchoolChange = async () => {
  query.teachingClassId = undefined
  gradebook.value = null
  scores.value = []
  teachingClasses.value = query.schoolId
    ? (await AiEducationApi.getTeachingClassList({ schoolId: query.schoolId })) || []
    : []
}
const searchStudents = async (keyword = '') => {
  const result: any = await request.get({
    url: '/member/user/page',
    params: { pageNo: 1, pageSize: 30, nickname: keyword || undefined }
  })
  students.value = result?.list || result || []
}
const loadGradebook = async () => {
  if (!query.teachingClassId) return
  loading.value = true
  try {
    gradebook.value = await UniversityApi.getOrCreateGradebook({
      teachingClassId: query.teachingClassId
    })
    scores.value = (await UniversityApi.getGradeScores(gradebook.value.id)) || []
    await searchStudents()
  } finally {
    loading.value = false
  }
}
const addScoreRow = () => {
  if (!gradebook.value) return
  scores.value.push({
    gradebookId: gradebook.value.id,
    studentId: 0 as any,
    usualScore: 0,
    homeworkScore: 0,
    examScore: 0,
    absentExam: false
  })
}
const saveScores = async () => {
  if (!gradebook.value) return
  const payload = scores.value.filter((s) => s.studentId)
  if (!payload.length) {
    message.warning('请至少添加一条成绩')
    return
  }
  saving.value = true
  try {
    await UniversityApi.saveGradeScores({
      gradebookId: gradebook.value.id,
      scores: payload.map((s) => ({
        studentId: s.studentId,
        usualScore: s.usualScore,
        homeworkScore: s.homeworkScore,
        examScore: s.examScore,
        absentExam: s.absentExam,
        remark: s.remark
      }))
    })
    message.success('成绩已保存')
    await loadGradebook()
  } finally {
    saving.value = false
  }
}
const handleSubmit = async () => {
  if (!gradebook.value) return
  await UniversityApi.submitGradebook(gradebook.value.id)
  message.success('已提交审核')
  await loadGradebook()
}
const handleFirstReview = async () => {
  if (!gradebook.value) return
  await UniversityApi.firstReviewGradebook(gradebook.value.id)
  message.success('初审通过')
  await loadGradebook()
}
const handleFinalReview = async () => {
  if (!gradebook.value) return
  await UniversityApi.finalReviewGradebook(gradebook.value.id)
  message.success('终审通过并已发布')
  await loadGradebook()
}
const openReject = () => {
  rejectReason.value = ''
  rejectVisible.value = true
}
const submitReject = async () => {
  if (!gradebook.value) return
  saving.value = true
  try {
    await UniversityApi.rejectGradebook({
      gradebookId: gradebook.value.id,
      rejectReason: rejectReason.value
    })
    message.success('已驳回')
    rejectVisible.value = false
    await loadGradebook()
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  schools.value = (await AiEducationApi.getSchoolList()) || []
})
</script>
